import { useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { ChevronRight, Check, Copy, ExternalLink, X } from 'lucide-react';

import { Spinner } from '@/components/ui/spinner';
import { AiConfidenceChip } from '@/components/ui/ai-confidence-chip';
import { ApiError } from '@/lib/api/client';
import {
  useCitationSourceQuery,
  type CitationRenderHint,
  type CitationSource,
} from '@/lib/api/citations';
import type { CitationChipData } from '@/components/ui/citation-chip';
import { cn } from '@/lib/utils/cn';
import { stripMarkdownEscapes } from '@/lib/utils/ai-text';

/**
 * T-0093 / T-0055 / E10 — Citation source modal (ADR-002, "Citation
 * round-trip"). The backend resolver landed in PR #132 (T-0055); this
 * modal is now end-to-end wired against the real endpoint.
 *
 * Visual contract: `design_handoff_caliber_v2/prototype/modals.js`
 * (`openCitation`) + `design_handoff_caliber_v2/README.md` §8 pattern 4.
 *
 * Behavior:
 *   - Fetches the passage via
 *     `GET /api/v1/engagements/:engagement_id/citations/:citation_id/source`
 *     when opened. The endpoint returns
 *     `{document_id?, document_title, section?, page?, cell?, full_passage,
 *       cited_span_start, cited_span_end}` — `cited_span_start/end` are
 *     character offsets into `full_passage`.
 *   - Renders the full passage with the cited span highlighted in
 *     `bg-cite-highlight` (yellow). The breadcrumb under the doc title
 *     surfaces "doc → section → page" (or doc → cell for spreadsheets)
 *     as a Crumb row — clearer than the comma-joined string the v1 modal
 *     used.
 *   - Copy-citation affordance writes a stable "Doc · §x · p.y · 0.9z"
 *     string to the clipboard. Useful for surfacing the citation in
 *     external tools (Notion, email, Slack) without re-typing.
 *   - Open-document deep link wires to /engagements/:id/documents/:doc
 *     when the API returns a document_id. The link uses
 *     `target="_self"` so the user lands on the doc detail route.
 *   - 404 / broken citations show the "Source document was deleted or
 *     not accessible" empty state with the original chip metadata still
 *     visible. The endpoint collapses off-engagement + missing-citation
 *     into the same 404 by design (existence-leak protection).
 *   - Keyboard: Esc closes, Tab cycles focus (Radix Dialog handles both
 *     focus trap and the Esc binding for us).
 *   - Respects `prefers-reduced-motion` via the `motion-safe:`/`motion-reduce:`
 *     prefix on the animation classes.
 *
 * Refinements vs. T-0093:
 *   - Breadcrumb is now a row of `Crumb` chips separated by chevrons
 *     rather than the comma-joined string the original used. The README
 *     §8 pattern 4 explicitly calls for "breadcrumb (doc → section →
 *     page)" — the chevron-separated row is the standard pattern for
 *     hierarchical breadcrumbs.
 *   - Copy-citation + Open-document affordances are footer buttons.
 *     Both are exposed in the prototype as bottom-row actions in the
 *     drawer; T-0093's modal had only a Close button.
 *   - Confidence chip moves into the header so users see calibration
 *     at a glance without scrolling to the bottom breadcrumb.
 *
 * Why Radix Dialog: the project already depends on `@radix-ui/react-dialog`
 * but hasn't wrapped it in a shared `Dialog` primitive yet. The two existing
 * "inline section reveal" modals (cell-annotation, verbModal) were ported
 * verbatim from the prototype and predate the design-handoff awareness.
 * Citations are the load-bearing UI primitive (CLAUDE.md hard rule #3 —
 * built once, reused everywhere); using Radix here gives us a real focus
 * trap, real aria-modal semantics, and proper scrim/portal rendering. The
 * inline-section approach degraded those accessibility props.
 */

export interface CitationSourceModalProps {
  engagementId: string;
  /**
   * The citation being viewed. Either:
   *   - The original chip metadata (`doc_label`, `section`, `page`, etc.)
   *     used as the fallback display when the API returns 404 / errors.
   *   - The full citation id is used as the fetch key.
   */
  cite: CitationChipData;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CitationSourceModal({
  engagementId,
  cite,
  open,
  onOpenChange,
}: CitationSourceModalProps) {
  // Only fetch when the modal is actually open so we don't burn quota
  // pre-fetching every chip on a page.
  const query = useCitationSourceQuery(engagementId, cite.id, { enabled: open });
  // Local "copied" feedback — flips back to the default after a short
  // delay so the user sees the confirmation tick.
  const [copied, setCopied] = useState(false);

  async function copyCitation() {
    const text = formatCitationString(cite, query.data);
    try {
      await globalThis.navigator?.clipboard?.writeText(text);
      setCopied(true);
      globalThis.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard write can fail in non-secure contexts (e.g. http:// preview).
      // Silently swallow — the user can still select-copy from the passage.
    }
  }

  // Treat 404 + the chip's own `status === 'broken'` flag as the same
  // "broken citation" state. The chip metadata is the fallback shown
  // either way.
  const isBroken =
    cite.status === 'broken' || (query.error instanceof ApiError && query.error.status === 404);

  const titleId = `cite-source-title-${cite.id}`;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-scrim',
            // Subtle fade — disabled under prefers-reduced-motion via the
            // motion-safe: prefix (motion-reduce gets no transition).
            'opacity-0 motion-safe:transition-opacity motion-safe:duration-150',
            'data-[state=open]:opacity-100',
          )}
        />
        <Dialog.Content
          aria-labelledby={titleId}
          className={cn(
            // Center; cap width + height; modal radius from tokens.
            'fixed left-1/2 top-1/2 z-50 grid w-[min(720px,92vw)] max-h-[85vh]',
            '-translate-x-1/2 -translate-y-1/2 grid-rows-[auto,1fr,auto]',
            'overflow-hidden rounded-modal bg-paper-0 shadow-modal',
            'opacity-0 motion-safe:transition-opacity motion-safe:duration-150',
            'data-[state=open]:opacity-100',
          )}
        >
          <CitationSourceHeader
            titleId={titleId}
            cite={cite}
            // Hide the live confidence in the header until we have data —
            // the chip's own confidence ships in via metadata too.
            data={query.data}
            isBroken={isBroken}
          />

          <div className="overflow-y-auto px-6 py-5">
            {query.isLoading ? (
              <div className="flex items-center gap-2 text-sm text-ink-500">
                <Spinner /> Loading source passage…
              </div>
            ) : isBroken ? (
              <BrokenSourceEmptyState cite={cite} />
            ) : query.error ? (
              <ApiErrorState cite={cite} />
            ) : query.data ? (
              <PassageView data={query.data} />
            ) : null}
          </div>

          <div className="flex items-center justify-between gap-3 border-t border-ink-100 bg-paper-50 px-6 py-3">
            <div className="text-meta text-ink-500">
              Citation id <span className="font-mono">{cite.id}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={copyCitation}
                aria-label="Copy citation to clipboard"
                disabled={query.isLoading}
                className={cn(
                  'inline-flex h-9 items-center gap-1.5 rounded-card border border-ink-200 bg-paper-0 px-3 text-body-sm font-medium text-ink-700',
                  'hover:bg-paper-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                )}
              >
                {copied ? (
                  <>
                    <Check className="h-3.5 w-3.5" aria-hidden="true" /> Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-3.5 w-3.5" aria-hidden="true" /> Copy citation
                  </>
                )}
              </button>
              {query.data?.document_id ? (
                <a
                  href={`/engagements/${engagementId}/documents/${query.data.document_id}`}
                  className={cn(
                    'inline-flex h-9 items-center gap-1.5 rounded-card border border-ink-200 bg-paper-0 px-3 text-body-sm font-medium text-ink-700',
                    'hover:bg-paper-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
                  )}
                >
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" /> Open document
                </a>
              ) : null}
              <Dialog.Close
                className={cn(
                  'inline-flex h-9 items-center rounded-card bg-accent-amber-600 px-4 text-body-sm font-semibold text-paper-0',
                  'hover:bg-accent-amber-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
                )}
              >
                Close
              </Dialog.Close>
            </div>
          </div>

          {/* Visually styled close button, accessible via Esc + Tab. */}
          <Dialog.Close
            aria-label="Close citation source"
            className={cn(
              'absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-card',
              'text-ink-500 hover:bg-paper-100 hover:text-ink-900',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
            )}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function CitationSourceHeader({
  titleId,
  cite,
  data,
  isBroken,
}: {
  titleId: string;
  cite: CitationChipData;
  data: CitationSource | undefined;
  isBroken: boolean;
}) {
  // Prefer the live document title from the API; fall back to the chip
  // doc_label so the header always renders something meaningful.
  const title = stripMarkdownEscapes(data?.document_title ?? cite.doc_label);

  return (
    <div className="flex items-start justify-between gap-3 border-b border-ink-100 px-6 py-4 pr-14">
      <div className="min-w-0 flex-1">
        <div className="text-meta uppercase tracking-wide text-ink-500">Citation source</div>
        <Dialog.Title
          id={titleId}
          className={cn(
            'mt-1 truncate text-h2 font-semibold text-ink-900',
            isBroken && 'text-cite-broken line-through decoration-1',
          )}
        >
          {title}
        </Dialog.Title>
        <Breadcrumb cite={cite} data={data} />
      </div>
      {!isBroken ? (
        <div className="shrink-0">
          <AiConfidenceChip value={cite.confidence} />
        </div>
      ) : null}
    </div>
  );
}

/**
 * Breadcrumb chip row under the doc title — "Doc › §4.3.2 › p.18".
 * Each crumb is its own chip-style span; the chevron lives between
 * them so the hierarchy reads top-to-bottom for screen readers.
 */
function Breadcrumb({ cite, data }: { cite: CitationChipData; data: CitationSource | undefined }) {
  const docLabel = stripMarkdownEscapes(data?.document_title ?? cite.doc_label);
  const section = data?.section ?? cite.section ?? null;
  const page = data?.page ?? cite.page ?? null;
  const cell = data?.cell ?? cite.cell ?? null;

  const crumbs: { key: string; label: string; mono?: boolean }[] = [
    { key: 'doc', label: docLabel },
  ];
  if (section) crumbs.push({ key: 'sec', label: `§${stripMarkdownEscapes(section)}`, mono: true });
  if (typeof page === 'number' && Number.isFinite(page))
    crumbs.push({ key: 'page', label: `p.${page}`, mono: true });
  if (cell) crumbs.push({ key: 'cell', label: cell, mono: true });

  return (
    <nav aria-label="Citation breadcrumb" className="mt-1.5 flex flex-wrap items-center gap-1">
      {crumbs.map((c, i) => (
        <span key={c.key} className="flex items-center gap-1">
          <span
            className={cn(
              'inline-flex max-w-[280px] truncate rounded-chip border border-ink-100 bg-paper-0 px-2 py-0.5 text-[11px] font-medium text-ink-700',
              c.mono && 'font-mono',
            )}
          >
            {c.label}
          </span>
          {i < crumbs.length - 1 ? (
            <ChevronRight className="h-3 w-3 text-ink-300" aria-hidden="true" />
          ) : null}
        </span>
      ))}
    </nav>
  );
}

/**
 * Plain-text citation string for the clipboard. Stable shape: doc label,
 * section (if present), page (if present), cell (if present), then
 * confidence. Round-trips into Slack / email / Notion as a single
 * recognizable cite.
 */
function formatCitationString(cite: CitationChipData, data: CitationSource | undefined): string {
  const docLabel = stripMarkdownEscapes(data?.document_title ?? cite.doc_label);
  const section = data?.section ?? cite.section ?? null;
  const page = data?.page ?? cite.page ?? null;
  const cell = data?.cell ?? cite.cell ?? null;
  const parts: string[] = [docLabel];
  if (section) parts.push(`§${stripMarkdownEscapes(section)}`);
  if (typeof page === 'number' && Number.isFinite(page)) parts.push(`p.${page}`);
  if (cell) parts.push(cell);
  parts.push(cite.confidence.toFixed(2));
  return parts.join(' · ');
}

/**
 * Render the full passage with the cited span highlighted in `--cite-highlight`.
 * The API returns `[cited_span_start, cited_span_end)` character offsets
 * into `full_passage`; we slice the string into three pieces (before /
 * highlighted / after) and apply the highlight class to the middle.
 *
 * Defensive clamping: bad offsets shouldn't crash the modal — if either
 * bound is out of range we just render the unhighlighted passage.
 */
const RENDER_HINT_LABEL: Record<CitationRenderHint, string> = {
  pdf_page: 'PDF page',
  markdown_section: 'Markdown section',
  xlsx_cell: 'Spreadsheet cell',
  docx_paragraph: 'Document paragraph',
};

const RENDER_HINT_DESCRIPTION: Record<CitationRenderHint, string> = {
  pdf_page: 'Paginated PDF — passage anchored to a page.',
  markdown_section: 'Parsed markdown — passage anchored to a section heading.',
  xlsx_cell: 'Spreadsheet — passage anchored to a specific cell.',
  docx_paragraph: 'Word document — passage anchored to a paragraph.',
};

function PassageView({ data }: { data: CitationSource }) {
  const { full_passage, cited_span_start, cited_span_end, render_hint } = data;

  // The offsets index the stored passage, so it is sliced first and each
  // piece loses its Markdown escapes ("No\." → "No.") afterwards (C2-33).
  const segments = useMemo(() => {
    const len = full_passage.length;
    const start = Math.max(0, Math.min(cited_span_start, len));
    const end = Math.max(start, Math.min(cited_span_end, len));
    if (start === end) return { before: stripMarkdownEscapes(full_passage), mid: '', after: '' };
    return {
      before: stripMarkdownEscapes(full_passage.slice(0, start)),
      mid: stripMarkdownEscapes(full_passage.slice(start, end)),
      after: stripMarkdownEscapes(full_passage.slice(end)),
    };
  }, [full_passage, cited_span_start, cited_span_end]);

  const hasHighlight = segments.mid.length > 0;
  // T-0125: switch inline renderer hint per the server-derived
  // `render_hint`. v1 surfaces it as a chip + description; future iterations
  // can swap the `<p>` for a paginated PDF viewer, an xlsx cell preview, etc.
  const hint = render_hint ?? null;

  return (
    <article className="text-body text-ink-700">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-meta uppercase tracking-wide text-ink-500" id="passage-label">
          Source passage
        </h3>
        <div className="flex items-center gap-2">
          {hint ? (
            <span
              className="inline-flex items-center rounded-chip border border-ink-100 bg-paper-0 px-2 py-0.5 text-[11px] font-medium text-ink-700"
              title={RENDER_HINT_DESCRIPTION[hint]}
            >
              {RENDER_HINT_LABEL[hint]}
            </span>
          ) : null}
          {hasHighlight ? (
            <span
              aria-hidden="true"
              className="inline-flex items-center gap-1 text-meta text-ink-500"
            >
              <span className="inline-block h-2.5 w-2.5 rounded-sm bg-cite-highlight" />
              Cited span highlighted
            </span>
          ) : null}
        </div>
      </div>
      <p
        className="mt-2 whitespace-pre-wrap rounded-card border border-ink-100 bg-paper-50 p-4 leading-relaxed"
        aria-labelledby="passage-label"
      >
        {segments.before}
        {hasHighlight ? (
          // Use a rounded yellow highlight that wraps cleanly across line
          // breaks. `decoration-cite-highlight` would only underline; the
          // background + small ring conveys "this is the cited span".
          <mark
            className="rounded-[2px] bg-cite-highlight px-1 py-[1px] font-medium text-ink-900 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.04)]"
            aria-label="Cited span"
          >
            {segments.mid}
          </mark>
        ) : null}
        {segments.after}
      </p>
    </article>
  );
}

function BrokenSourceEmptyState({ cite }: { cite: CitationChipData }) {
  return (
    <div
      role="status"
      className="space-y-3 rounded-card border border-dashed border-ink-200 bg-paper-50 p-5"
    >
      <h3 className="text-h3 font-semibold text-cite-broken">
        Source document was deleted or not accessible
      </h3>
      <p className="text-body-sm text-ink-700">
        The document this citation points to is no longer in the engagement&apos;s evidence store.
        The original citation metadata is shown below for reference.
      </p>
      <CitationMetadataList cite={cite} />
    </div>
  );
}

function ApiErrorState({ cite }: { cite: CitationChipData }) {
  return (
    <div
      role="status"
      className="space-y-3 rounded-card border border-dashed border-ink-200 bg-paper-50 p-5"
    >
      <h3 className="text-h3 font-semibold text-ink-900">Source view unavailable</h3>
      <p className="text-body-sm text-ink-700">
        The citation source could not be resolved right now. The original citation metadata is shown
        below for reference; try reopening the chip in a moment.
      </p>
      <CitationMetadataList cite={cite} />
    </div>
  );
}

function CitationMetadataList({ cite }: { cite: CitationChipData }) {
  return (
    <dl className="grid grid-cols-[max-content,1fr] gap-x-4 gap-y-1 text-body-sm">
      <dt className="text-ink-500">Document</dt>
      <dd className="font-medium text-ink-900">{stripMarkdownEscapes(cite.doc_label)}</dd>
      {cite.section ? (
        <>
          <dt className="text-ink-500">Section</dt>
          <dd className="font-mono text-ink-700">§{stripMarkdownEscapes(cite.section)}</dd>
        </>
      ) : null}
      {typeof cite.page === 'number' ? (
        <>
          <dt className="text-ink-500">Page</dt>
          <dd className="font-mono text-ink-700">p.{cite.page}</dd>
        </>
      ) : null}
      {cite.cell ? (
        <>
          <dt className="text-ink-500">Cell</dt>
          <dd className="font-mono text-ink-700">{cite.cell}</dd>
        </>
      ) : null}
      <dt className="text-ink-500">Confidence</dt>
      <dd className="font-mono text-ink-700">{cite.confidence.toFixed(2)}</dd>
    </dl>
  );
}
